import React, { useState } from 'react';
import clsx from 'clsx';
import {
  useDeleteFileMutation,
  useGetFileDownloadUrlMutation,
} from 'api/assets/assetsServices';
import { PiDownloadSimpleFill } from 'react-icons/pi';
import { FaRegTrashAlt, FaSpinner } from 'react-icons/fa';
import { formatFileSize } from 'lib/fileUploadConstants';
import { convertToReadableDate } from 'utils/dateUtils/dateUtils';
import FileIconContainer from './FileIconContainer';
import { type UploadedFile } from 'interfaces/file/File.interface';

interface DownloadableFileItemProps {
  file: UploadedFile;
  assetId: string | number;
  onDelete?: (fileId: number) => void;
  showActions?: boolean;
}

const DownloadableFileItem: React.FC<DownloadableFileItemProps> = ({
  file,
  assetId,
  onDelete,
  showActions = true,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    mutate: getDownloadUrl,
    status,
    reset,
  } = useGetFileDownloadUrlMutation();
  const { mutate: deleteMutate, status: deleteStatus } =
    useDeleteFileMutation(assetId);

  const handleDownload = (): void => {
    setIsDownloading(true);
    getDownloadUrl(
      { assetId, fileId: file.id },
      {
        onSuccess: (data) => {
          if (data.download_url) {
            const link = document.createElement('a');
            link.href = data.download_url;
            link.setAttribute('download', data.file_metadata.name ?? file.name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          setIsDownloading(false);
          reset();
        },
        onError: (error) => {
          console.error('Failed to get download url:', error);
          setIsDownloading(false);
        },
      }
    );
  };

  const handleDelete = (): void => {
    deleteMutate(
      { fileId: file.id },
      {
        onSuccess: () => {
          onDelete?.(file.id);
        },
        onError: (error) => {
          console.error('Failed to delete file:', error);
        },
      }
    );
  };

  const isDeleting = deleteStatus === 'pending';
  const downloadInProgress = status === 'pending' || isDownloading;

  return (
    <div
      className={clsx(
        'flex flex-1 flex-row items-center justify-between rounded-md border px-4 py-3 transition-all duration-200',
        'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
      )}
    >
      <div className="flex flex-col flex-1 w-100">
        <div className="flex items-start gap-2">
          <FileIconContainer fileName={file.name} />
          <div className="flex flex-col flex-1 gap-0.5">
            <span
              className={clsx(
                'text-title text-sm font-bold truncate text-gray-60'
              )}
            >
              {file.name}
            </span>
            <span className="text-xs text-body-secondary flex items-center text-gray-40">
              {formatFileSize(file.file_size)}
              {file.uploaded_at && (
                <>
                  <div className="mx-2 w-1 h-1 bg-primary opacity-25 rounded" />
                  Uploaded{' '}
                  {convertToReadableDate(file.uploaded_at, { onlyDate: true })}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {showActions && (
        <div className="flex items-center gap-2 ml-2">
          <button
            onClick={handleDownload}
            disabled={downloadInProgress || isDeleting}
            className={clsx(
              'p-1 rounded-md transition-colors',
              'text-gray-60 hover:text-primary-dark hover:bg-primary/10',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Download file"
            title="Download file"
          >
            {downloadInProgress ? (
              <FaSpinner className="animate-spin" size={20} />
            ) : (
              <PiDownloadSimpleFill size={20} />
            )}
          </button>

          <button
            onClick={handleDelete}
            disabled={isDeleting || downloadInProgress}
            className={clsx(
              'p-1 rounded-md transition-colors',
              'text-error hover:text-error-dark hover:bg-error/10',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Delete file"
            title="Delete file"
          >
            {isDeleting ? (
              <FaSpinner className="animate-spin" size={20} />
            ) : (
              <FaRegTrashAlt size={20} />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default DownloadableFileItem;
